import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, PREVISIONES, TIPOS_PLAN, REGIONES, formatError, waLink } from "@/lib/site";
import { WaIcon } from "@/components/landing/WaIcon";
import { btnPrimary } from "./ui";

const empty = { nombre: "", telefono: "+56 9 ", email: "", prevision: "", tipo_plan: "", region: "", consentimiento: false };
const lbl = "mb-1.5 block text-[17px] font-semibold text-[#1F2A33]";

const Radios = ({ name, options, value, onChange }) => (
  <div className="grid grid-cols-3 gap-2" role="radiogroup">
    {options.map((o) => (
      <label key={o} className={`flex min-h-[52px] cursor-pointer items-center justify-center rounded-xl border-2 text-[17px] font-semibold transition-colors focus-within:outline focus-within:outline-[3px] focus-within:outline-[#1FA89A] ${value === o ? "border-[#0B3C5D] bg-[#0B3C5D] text-white" : "border-[#D6E2EA] bg-white text-[#1F2A33] hover:border-[#0B3C5D]"}`}>
        <input type="radio" name={name} value={o} checked={value === o} onChange={() => onChange(o)} className="sr-only" data-testid={`form-${name}-${o.toLowerCase()}`} />{o}
      </label>
    ))}
  </div>
);

const buildMsg = (f) => `Hola, soy ${f.nombre}. Dejé mis datos en la web. Hoy tengo ${f.prevision}, busco un plan ${f.tipo_plan.toLowerCase()} y vivo en la región ${f.region}. Quiero asesorarme.`;

export const ContactForm = () => {
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.telefono.replace(/\D/g, "").length < 9) return toast.error("Revisa tu número de teléfono.");
    if (f.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) return toast.error("Revisa tu correo o déjalo en blanco.");
    if (!f.prevision || !f.tipo_plan || !f.region) return toast.error("Completa todas las preguntas.");
    if (!f.consentimiento) return toast.error("Debes aceptar que te contactemos para continuar.");
    setLoading(true);
    try {
      await http.post("/leads", f);
      setDone(f);
      setF(empty);
      window.open(waLink(buildMsg(f)), "_blank", "noopener");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (done) return (
    <div className="rounded-2xl bg-white p-6 text-center sm:p-10" data-testid="form-success" role="status">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#E3F5F2]"><Check className="h-8 w-8 text-[#13766C]" strokeWidth={3} /></div>
      <h3 className="mt-5 text-[26px] font-extrabold text-[#0B3C5D]">¡Gracias, {done.nombre.split(" ")[0]}!</h3>
      <p className="mt-3 text-[18px] leading-relaxed text-[#4A5864]">Recibimos tus datos y te contactaremos pronto. Si WhatsApp no se abrió, toca el botón:</p>
      <a href={waLink(buildMsg(done))} target="_blank" rel="noopener noreferrer" className={`${btnPrimary} mt-6 w-full`} data-testid="form-success-whatsapp-btn"><WaIcon /> Abrir WhatsApp</a>
      <button onClick={() => setDone(null)} className="mt-4 text-[17px] font-semibold text-[#0B3C5D] underline underline-offset-4" data-testid="form-reset-btn">Enviar otros datos</button>
    </div>
  );

  return (
    <form onSubmit={submit} noValidate className="space-y-5 rounded-2xl bg-white p-6 sm:p-8" data-testid="contact-form">
      <label className="block"><span className={lbl}>Nombre</span>
        <input required className="field" value={f.nombre} onChange={(e) => set("nombre", e.target.value)} autoComplete="name" data-testid="form-nombre" /></label>
      <label className="block"><span className={lbl}>Teléfono / WhatsApp</span>
        <input required type="tel" inputMode="tel" className="field" value={f.telefono} onChange={(e) => set("telefono", e.target.value)} autoComplete="tel" data-testid="form-telefono" /></label>
      <label className="block"><span className={lbl}>Correo <span className="font-normal text-[#4A5864]">(opcional)</span></span>
        <input type="email" className="field" value={f.email} onChange={(e) => set("email", e.target.value.trim())} autoComplete="email" placeholder="Para enviarte una confirmación" data-testid="form-email" /></label>
      <fieldset><legend className={lbl}>¿Hoy tienes Isapre o Fonasa?</legend>
        <Radios name="prevision" options={PREVISIONES} value={f.prevision} onChange={(v) => set("prevision", v)} /></fieldset>
      <fieldset><legend className={lbl}>¿Buscas plan individual o familiar?</legend>
        <div className="[&>div]:grid-cols-2"><Radios name="tipo_plan" options={TIPOS_PLAN} value={f.tipo_plan} onChange={(v) => set("tipo_plan", v)} /></div></fieldset>
      <label className="block"><span className={lbl}>Región</span>
        <select className="field" value={f.region} onChange={(e) => set("region", e.target.value)} data-testid="form-region">
          <option value="" disabled>Elige tu región…</option>
          {REGIONES.map((r) => <option key={r}>{r}</option>)}
        </select></label>
      <label className="flex cursor-pointer items-start gap-3 text-[16px] leading-relaxed text-[#1F2A33]">
        <input type="checkbox" checked={f.consentimiento} onChange={(e) => set("consentimiento", e.target.checked)} className="mt-1 h-6 w-6 shrink-0 accent-[#0B3C5D]" data-testid="form-consentimiento" />
        <span>Acepto que me contacten para recibir asesoría y el uso de mis datos para ese fin. <a href="/privacidad" className="font-semibold text-[#0B3C5D] underline">Ver política de privacidad</a>.</span>
      </label>
      <button type="submit" disabled={loading} className={`${btnPrimary} w-full disabled:opacity-70`} data-testid="form-submit">
        {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : "Enviar y hablar por WhatsApp"}
      </button>
      <p className="text-center text-[15px] text-[#4A5864]">No pedimos información médica.</p>
    </form>
  );
};
